import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import PreloaderIcon from 'components/Preloader';

import './style.scss';

interface FormRequestBlockProps {
  id?: string; // for heap analytics mostly
  children?: React.ReactNode;
  title: string;
  statusDetail?: string | React.ReactNode;
  text: string | React.ReactNode;
  placeholder?: string;
  inputValue?: string;
  submitValue?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: 'text' | 'email' | 'url' | 'number' | 'tel';
  required?: boolean;
  pattern?: string;
  onSubmit?: (e: React.SyntheticEvent) => void;
  onChange?: (value: string) => void;
  styles?: { form?: React.CSSProperties };
  error?: string;
  // Optional attached footer — renders inside the same card with a top
  // hairline + tinted surface-2 background. Use for supplementary content
  // like SDK snippets that belong with the form's primary action.
  footer?: React.ReactNode;
  // Drops footer padding so the footer's content can sit edge-to-edge —
  // useful when the content already has its own internal padding (e.g. a
  // CodeBlock).
  flushFooter?: boolean;
}

// FormRequestBlock — a labelled card with a single inline {Input + submit}
// pair. Consumers can pass `children` to replace the default body with a
// custom layout (multiple inputs, selects, etc.). Buttons/Inputs inside
// children should use the shared `Button` and `Input` primitives so the
// styling stays consistent.
const FormRequestBlock = ({
  children,
  title,
  statusDetail,
  text,
  placeholder,
  required,
  inputValue = '',
  submitValue = 'Save',
  disabled = false,
  loading = false,
  type = 'text',
  pattern,
  styles,
  onSubmit = () => {
    return;
  },
  onChange = () => {
    return;
  },
  error,
  footer,
  flushFooter = false,
}: FormRequestBlockProps) => {
  const formClass = `form-request-block${loading ? ' loading' : ''}`;
  return (
    <form className={formClass} onSubmit={onSubmit}>
      <div>
        <div className="form-request-block-preloader">
          <PreloaderIcon />
        </div>
        <div className="flex w-full flex-row">
          <h3 className="form-request-block-title flex-1">{title}</h3>
          {statusDetail && <div className="form-request-block-status">{statusDetail}</div>}
        </div>

        <div className="form-request-block-text">{text}</div>
        <div className="form-request-block-form" style={styles?.form}>
          {children || (
            <>
              <div className="relative flex-1">
                <LinkIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 fill-[var(--text-muted)]"
                  title="Link"
                />
                <Input
                  type={type}
                  required={required}
                  pattern={pattern}
                  className="w-full pl-10"
                  placeholder={placeholder}
                  onChange={(e) => onChange(e.target.value.trim())}
                  value={inputValue}
                  disabled={loading || disabled}
                />
              </div>
              <Button type="submit" variant="secondary" disabled={loading || disabled}>
                {submitValue}
              </Button>
            </>
          )}
        </div>
      </div>

      {footer && (
        <div className={`form-request-block-footer${flushFooter ? ' flush' : ''}`}>{footer}</div>
      )}

      {error && (
        <div className="form-request-block__error">
          <WarningIcon width={60} height={60} /> <span style={{ marginLeft: '8px' }}>{error}</span>
        </div>
      )}
    </form>
  );
};

export default FormRequestBlock;
